import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import consultantApi from '../../services/consultantApi';
import './CompanyManagement.css';
import '../Employer/Employer.css';

const CompanyManagement = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [companies, setCompanies] = useState({ pending: [], rejected: [], active: [], all: [] });
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [showAvailableModal, setShowAvailableModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchMyCompanies();
  }, []);

  const fetchMyCompanies = async () => {
    try {
      setLoading(true);
      const response = await consultantApi.getMyCompanies();
      // Backend returns nested data structure: response.data.data
      setCompanies(response.data.data || response.data);
    } catch (error) {
      toast.error('Failed to fetch companies');
      console.error('Fetch companies error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableCompanies = async () => {
    try {
      setLoading(true);
      // console.log('Searching for companies with term:', searchTerm);
      
      // Try with search term first
      let response = await consultantApi.getAvailableCompanies({ search: searchTerm });
      // console.log('Available companies API response (with search):', response);
      
      // If no results with search term, try without search to see if any companies are available
      if (response.data && response.data.length === 0 && searchTerm.trim()) {
        // console.log('No results with search term, trying without search...');
        response = await consultantApi.getAvailableCompanies({});
        // console.log('Available companies API response (without search):', response);
      }
      
      // console.log('Available companies data:', response.data);
      // This endpoint returns data directly (not nested): response.data
      setAvailableCompanies(response.data);
      setShowAvailableModal(true);
    } catch (error) {
      toast.error('Failed to fetch available companies');
      console.error('Fetch available companies error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async (companyId) => {
    try {
      await consultantApi.sendCompanyRequest(companyId);
      toast.success('Request sent successfully');
      setShowAvailableModal(false);
      fetchMyCompanies();
    } catch (error) {
      toast.error(error.message || 'Failed to send request');
      console.error('Send request error:', error);
    }
  };

  const handleLeaveCompany = async (associationId) => {
    if (!window.confirm('Are you sure you want to leave this company?')) {
      return;
    }

    try {
      await consultantApi.leaveCompany(associationId);
      toast.success('Successfully left the company');
      fetchMyCompanies();
    } catch (error) {
      toast.error('Failed to leave company');
      console.error('Leave company error:', error);
    }
  };

  const getStatusConfig = (status) => {
    const map = {
      accepted: { label: 'Active',    color: 'success', icon: 'check_circle' },
      pending:  { label: 'Pending',   color: 'warning', icon: 'schedule' },
      rejected: { label: 'Rejected',  color: 'danger',  icon: 'cancel' },
    };
    return map[status] || map.pending;
  };

  const renderCompanyCard = (companyData) => {
    const { associationId, company, status, requestedAt, respondedAt, rejectionReason } = companyData;
    const cfg = getStatusConfig(status);
    const initials = company.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

    return (
      <div key={associationId} className={`cm-card cm-card-${status}`}>
        <div className="cm-card-top">
          <div className="cm-card-avatar">
            {company.logo
              ? <img src={company.logo} alt={company.name} />
              : <span>{initials}</span>
            }
          </div>
          <div className="cm-card-title">
            <h3>{company.name}</h3>
            <p>{company.industry?.name || 'No industry'}</p>
          </div>
          <span className={`cm-badge ${cfg.color}`}>
            <span className="material-symbols-outlined">{cfg.icon}</span>
            {cfg.label}
          </span>
        </div>

        {company.description && (
          <p className="cm-card-desc">{company.description.substring(0, 120)}{company.description.length > 120 ? '…' : ''}</p>
        )}

        <div className="cm-card-meta">
          {company.companySize && (
            <span><span className="material-symbols-outlined">group</span>{company.companySize}</span>
          )}
          {company.address?.city && (
            <span><span className="material-symbols-outlined">location_on</span>{company.address.city}</span>
          )}
          <span><span className="material-symbols-outlined">calendar_today</span>Requested {new Date(requestedAt).toLocaleDateString()}</span>
        </div>

        {rejectionReason && (
          <div className="cm-rejection">
            <span className="material-symbols-outlined">error</span>
            <span><strong>Reason:</strong> {rejectionReason}</span>
          </div>
        )}

        {status === 'accepted' && (
          <div className="cm-card-actions">
            <button className="cm-btn-danger" onClick={() => handleLeaveCompany(associationId)}>
              <span className="material-symbols-outlined">logout</span>
              Leave Company
            </button>
          </div>
        )}
      </div>
    );
  };

  const getCompaniesForTab = () => {
    switch (activeTab) {
      case 'pending':  return companies.pending  || [];
      case 'rejected': return companies.rejected || [];
      case 'active':   return companies.active   || [];
      default:         return companies.all      || [];
    }
  };

  const tabList = [
    { key: 'all',      label: 'All',      count: companies.all?.length     || 0 },
    { key: 'active',   label: 'Active',   count: companies.active?.length  || 0, dot: 'green' },
    { key: 'pending',  label: 'Pending',  count: companies.pending?.length || 0, dot: 'orange' },
    { key: 'rejected', label: 'Rejected', count: companies.rejected?.length|| 0, dot: 'red' },
  ];

  return (
    <div className="cm-container">
      {/* Page Header */}
      <div className="cm-header">
        <div>
          <h1 className="cm-title">Companies</h1>
          <p className="cm-subtitle">Manage your company associations and requests</p>
        </div>
        <button className="cm-cta-btn" onClick={fetchAvailableCompanies}>
          <span className="material-symbols-outlined">add</span>
          Request Association
        </button>
      </div>

      {/* Tabs */}
      <div className="cm-tabs">
        {tabList.map(t => (
          <button
            key={t.key}
            className={`cm-tab ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.dot && <span className={`cm-tab-dot ${t.dot}`}></span>}
            {t.label}
            <span className="cm-tab-count">{t.count}</span>
          </button>
        ))}
      </div>

      {/* Cards Grid */}
      {loading ? (
        <div className="cm-loading">
          <div className="cm-spinner"></div>
          <p>Loading companies…</p>
        </div>
      ) : getCompaniesForTab().length === 0 ? (
        <div className="cm-empty">
          <span className="material-symbols-outlined">business_center</span>
          <h3>No companies found</h3>
          <p>Request an association to get started</p>
          <button className="cm-cta-btn" onClick={fetchAvailableCompanies}>
            <span className="material-symbols-outlined">add</span>
            Request Association
          </button>
        </div>
      ) : (
        <div className="cm-grid">
          {getCompaniesForTab().map(renderCompanyCard)}
        </div>
      )}

      {/* Modal */}
      {showAvailableModal && (
        <div className="cm-modal-overlay" onClick={() => setShowAvailableModal(false)}>
          <div className="cm-modal" onClick={e => e.stopPropagation()}>
            <div className="cm-modal-header">
              <div>
                <h2>Find Companies</h2>
                <p>Search and send association requests</p>
              </div>
              <button className="cm-modal-close" onClick={() => setShowAvailableModal(false)}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="cm-modal-body">
              <div className="cm-search-row">
                <div className="cm-search-field">
                  <span className="material-symbols-outlined">search</span>
                  <input
                    type="text"
                    placeholder="Search companies by name..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && fetchAvailableCompanies()}
                  />
                </div>
                <button className="cm-cta-btn" onClick={fetchAvailableCompanies}>Search</button>
              </div>

              <div className="cm-modal-list">
                {availableCompanies.length === 0 ? (
                  <div className="cm-empty-sm">
                    <span className="material-symbols-outlined">search_off</span>
                    <p>No companies found</p>
                  </div>
                ) : availableCompanies.map(company => {
                  const initials = company.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                  return (
                    <div key={company._id} className="cm-modal-item">
                      <div className="cm-modal-item-avatar">
                        {company.logo
                          ? <img src={company.logo} alt={company.name} />
                          : <span>{initials}</span>
                        }
                      </div>
                      <div className="cm-modal-item-info">
                        <h4>{company.name}</h4>
                        <p>{company.industry?.name || company.companySize || 'Company'}</p>
                      </div>
                      <button className="cm-request-btn" onClick={() => handleSendRequest(company._id)}>
                        <span className="material-symbols-outlined">send</span>
                        Request
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyManagement;
